import React, { useState } from 'react';
import { useApp, AppView } from '../../context/AppContext';
import { OptionKey, AttemptAnswer } from '../../types';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  XCircle,
  HelpCircle,
  RotateCcw,
  Sparkles,
  BookOpen,
  Check,
  Award,
  ChevronRight,
  Lightbulb,
} from 'lucide-react';
import { useToast } from '../ui/Toast';
import { ConfirmModal } from '../common/ConfirmModal';

export const QuizRevisionView: React.FC = () => {
  const {
    currentUser,
    activeQuestionSet,
    setActiveQuestionSet,
    questionSets,
    attempts,
    questions,
    subjects,
    setCurrentView,
    fetchAttempts,
    questionLanguage,
    setQuestionLanguage,
  } = useApp();
  const { showToast } = useToast();

  const [revisionTab, setRevisionTab] = useState<'due' | 'mistakes' | 'weak' | 'forgotten'>('due');

  if (!activeQuestionSet) {
    const activeSets = questionSets.filter((qs) => qs.is_active);
    const setsWithAttempts = attempts.map((a) => a.question_set_id);
    const missedSets = attempts.filter((a) => a.wrong_answers > 0).map((a) => a.question_set_id);

    return (
      <div className="space-y-6 max-w-5xl mx-auto pb-16">
        {/* REVise Header Hero */}
        <div className="bg-white rounded-3xl p-6 border border-[#E5E9E9] shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2.5 py-0.5 rounded-full bg-[#E8FAF9] text-[#1b8e88] text-[11px] font-bold border border-[#56E8E1]/40 uppercase tracking-wider">
                Signature Learning Engine
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#111111]">REVise Flashcards & Memory Engine</h1>
            <p className="text-xs sm:text-sm text-[#5F6368] mt-1">
              Identify mistakes → Revise with instant explanations → Retry → Improve retention.
            </p>
          </div>
        </div>

        {/* 4 Signature Product Tabs */}
        <div className="w-full overflow-x-auto no-scrollbar border-b border-[#E5E9E9]">
          <div className="flex items-center gap-2 pb-2">
            {[
              { id: 'due', label: 'Due Today', count: activeSets.length },
              { id: 'mistakes', label: 'Mistakes to Review', count: missedSets.length },
              { id: 'weak', label: 'Weak Topics', count: 2 },
              { id: 'forgotten', label: 'Forgotten Sets', count: Math.max(0, activeSets.length - setsWithAttempts.length) },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setRevisionTab(tab.id as any)}
                className={`px-4 py-2 text-xs font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer ${
                  revisionTab === tab.id
                    ? 'bg-[#E8FAF9] text-[#1b8e88] border border-[#56E8E1]/40 shadow-2xs'
                    : 'bg-white text-[#5F6368] hover:text-[#111111] border border-[#E5E9E9]'
                }`}
              >
                <span>{tab.label}</span>
                <span className="px-1.5 py-0.2 rounded-full bg-[#1fb4ac] text-white text-[10px]">
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {activeSets.map((qs) => {
            const subject = subjects.find((s) => s.id === qs.subject_id);
            const qCount = questions.filter((q) => q.question_set_id === qs.id).length;

            return (
              <div key={qs.id} className="bg-white p-5 rounded-2xl border border-[#E5E9E9] shadow-xs flex flex-col justify-between space-y-3 hover:border-[#1fb4ac]/40 transition-all">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="px-2 py-0.5 text-[10px] font-bold text-[#1b8e88] bg-[#E8FAF9] rounded-md border border-[#56E8E1]/40 uppercase">
                      {subject?.name || 'General'}
                    </span>
                    <span className="text-xs font-bold text-[#5F6368]">{qCount} Questions</span>
                  </div>
                  <h3 className="text-base font-bold text-[#111111]">{qs.name}</h3>
                  <p className="text-xs text-[#5F6368] leading-relaxed">
                    Interactive flashcards with instant step-by-step solution reveals.
                  </p>
                </div>
                <div className="pt-2 flex items-center justify-between border-t border-[#E5E9E9]">
                  <span className="text-[11px] text-[#5F6368] font-medium">{qs.time_limit_minutes} mins estimated</span>
                  <button
                    onClick={() => {
                      setActiveQuestionSet(qs);
                      showToast(`Starting ${qs.name} in Revision Mode`, 'info');
                    }}
                    className="px-3.5 py-1.5 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] font-bold text-xs rounded-xl transition-all shadow-xs cursor-pointer flex items-center gap-1.5"
                  >
                    <span>Start Revision</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Get questions for this set
  const questionsList = Array.isArray(questions) ? questions : [];
  const setQuestions = questionsList.filter(
    (q) => q.question_set_id === activeQuestionSet.id && q.status === 'active'
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, OptionKey>>({});
  const [startTime] = useState(Date.now());

  const currentQ = setQuestions[currentIndex];
  const subject = subjects.find((s) => s.id === activeQuestionSet.subject_id);

  if (setQuestions.length === 0) {
    return (
      <div className="max-w-xl mx-auto text-center py-16 bg-white rounded-3xl border border-[#E5E9E9] p-8 shadow-[0_4px_20px_rgba(17,17,17,0.03)]">
        <HelpCircle className="w-12 h-12 text-[#5F6368]/50 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-[#111111] mb-2">No Questions Available</h3>
        <p className="text-[#5F6368] text-xs mb-6 leading-relaxed">
          This question set currently contains no questions. You can add questions from the Admin Console.
        </p>
        <button
          onClick={() => setCurrentView('student-dashboard')}
          className="px-5 py-2.5 bg-[#56E8E1] text-[#111111] hover:bg-[#34d5cd] rounded-xl text-xs font-bold cursor-pointer transition-all"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const selectedOpt = currentQ ? selectedAnswers[currentQ.id] : null;
  const isAnswered = selectedOpt !== undefined && selectedOpt !== null;
  const isCorrect = isAnswered && selectedOpt === currentQ?.correct_option;

  const handleSelectOption = (opt: OptionKey) => {
    if (!currentQ) return;
    setSelectedAnswers((prev) => ({
      ...prev,
      [currentQ.id]: opt,
    }));
  };

  const handleResetCurrent = () => {
    if (!currentQ) return;
    setSelectedAnswers((prev) => {
      const copy = { ...prev };
      delete copy[currentQ.id];
      return copy;
    });
    showToast('Choice cleared. Try answering again!', 'info');
  };

  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFinish = async (targetView: AppView = 'quiz-scorecard') => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    let correctCount = 0;
    let wrongCount = 0;
    let skippedCount = 0;

    const answersList: AttemptAnswer[] = setQuestions.map((q) => {
      const chosen = selectedAnswers[q.id] || null;
      if (!chosen) {
        skippedCount++;
        return {
          question_id: q.id,
          selected_option: null,
          correct_option: q.correct_option,
          is_correct: false,
        };
      }
      const match = chosen === q.correct_option;
      if (match) correctCount++;
      else wrongCount++;
      return {
        question_id: q.id,
        selected_option: chosen,
        correct_option: q.correct_option,
        is_correct: match,
      };
    });

    const elapsedSeconds = Math.max(1, Math.round((Date.now() - startTime) / 1000));
    const scorePct = setQuestions.length > 0 ? Math.round((correctCount / setQuestions.length) * 100) : 0;

    try {
      console.log('[Submission Step 1] Creating attempt via /api/attempts.php?action=create');
      const formData = new URLSearchParams();
      formData.append('question_set_id', String(activeQuestionSet.id));
      formData.append('mode', 'revision');

      const startRes = await fetch('/api/attempts.php?action=create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        credentials: 'include',
        body: formData.toString(),
      });
      const startData = await startRes.json().catch((err) => {
        console.error('JSON parse error on attempts.php response:', err);
        return null;
      });

      console.log('[Submission Step 1 Response]', startRes.status, startData);

      if (!startData?.success || !startData?.attempt_id) {
        throw new Error(startData?.error || `Failed to create attempt (HTTP ${startRes.status})`);
      }

      const serverAttemptId = startData.attempt_id;

      console.log('[Submission Step 2] Submitting answers via /api/submit_test.php for attempt ID:', serverAttemptId);
      const submitRes = await fetch('/api/submit_test.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          attempt_id: serverAttemptId,
          time_spent: elapsedSeconds,
          answers: selectedAnswers,
        }),
      });
      const submitData = await submitRes.json().catch((err) => {
        console.error('JSON parse error on submit_test.php response:', err);
        return null;
      });

      console.log('[Submission Step 2 Response]', submitRes.status, submitData);

      if (!submitRes.ok || !submitData?.success) {
        throw new Error(submitData?.error || `Failed to submit test answers (HTTP ${submitRes.status})`);
      }

      console.log('[Submission Step 3] Refreshing attempts via fetchAttempts()');
      await fetchAttempts();

      console.log(`[Submission Step 4] Success - Navigating to ${targetView}`);
      if (targetView === 'student-dashboard') {
        showToast('Revision progress saved!', 'success', 'Saved & Exited');
      } else {
        showToast(`Revision completed! Score: ${scorePct}%`, 'success', 'Session Saved');
      }
      setIsSubmitModalOpen(false);
      setShowExitConfirm(false);
      setCurrentView(targetView);
    } catch (e: any) {
      console.error('Revision submission error caught:', e);
      const errorMsg = typeof e === 'string' ? e : e?.message ? String(e.message) : 'Error completing revision submission';
      showToast(errorMsg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Content resolution with Hindi fallback to English
  const getQuestionText = () => {
    if (!currentQ) return '';
    if (questionLanguage === 'hi' && currentQ.question_text_hi?.trim()) {
      return currentQ.question_text_hi;
    }
    return currentQ.question_text;
  };

  const getOptionText = (key: OptionKey) => {
    if (!currentQ) return '';
    if (questionLanguage === 'hi') {
      if (key === 'A' && currentQ.option_a_hi?.trim()) return currentQ.option_a_hi;
      if (key === 'B' && currentQ.option_b_hi?.trim()) return currentQ.option_b_hi;
      if (key === 'C' && currentQ.option_c_hi?.trim()) return currentQ.option_c_hi;
      if (key === 'D' && currentQ.option_d_hi?.trim()) return currentQ.option_d_hi;
    }
    if (key === 'A') return currentQ.option_a;
    if (key === 'B') return currentQ.option_b;
    if (key === 'C') return currentQ.option_c;
    return currentQ.option_d;
  };

  const optionsList: { key: OptionKey; text: string }[] = currentQ
    ? [
        { key: 'A', text: getOptionText('A') },
        { key: 'B', text: getOptionText('B') },
        { key: 'C', text: getOptionText('C') },
        { key: 'D', text: getOptionText('D') },
      ]
    : [];

  const totalAnswered = Object.keys(selectedAnswers).length;
  const progressPercent = Math.round(((currentIndex + 1) / setQuestions.length) * 100);

  // Helper for windowed pagination buttons in Revision mode
  const getVisiblePages = () => {
    const total = setQuestions.length;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i);
    }
    const current = currentIndex;
    const pages: (number | 'ellipsis-left' | 'ellipsis-right')[] = [];

    let start = Math.max(0, current - 2);
    let end = Math.min(total - 1, current + 2);

    if (start <= 1) {
      start = 0;
      end = Math.min(total - 1, 4);
    } else if (end >= total - 2) {
      end = total - 1;
      start = Math.max(0, total - 5);
    }

    if (start > 0) {
      pages.push(0);
      if (start > 1) pages.push('ellipsis-left');
    }

    for (let i = start; i <= end; i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }

    if (end < total - 1) {
      if (end < total - 2) pages.push('ellipsis-right');
      pages.push(total - 1);
    }

    return pages;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-5 pb-24 md:pb-16">
      {/* 1. Practice Top Navigation & Progress Bar */}
      <div className="bg-white rounded-2xl border border-[#E5E9E9] p-4 sm:p-5 shadow-[0_4px_20px_rgba(17,17,17,0.03)] space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <button
              onClick={() => setShowExitConfirm(true)}
              className="p-2 rounded-xl text-[#5F6368] hover:text-[#111111] hover:bg-[#F0F4F4] transition-colors shrink-0 cursor-pointer"
              title="Return to dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#E8FAF9] text-[#1b8e88] border border-[#56E8E1]/40 flex items-center gap-1">
                  <BookOpen className="w-3 h-3 text-[#1fb4ac]" />
                  Revision Mode
                </span>
                {subject && (
                  <span className="text-xs text-[#5F6368] font-medium truncate hidden sm:inline">
                    • {subject.name}
                  </span>
                )}
              </div>
              <h1 className="text-sm sm:text-base font-bold text-[#111111] truncate mt-0.5">
                {activeQuestionSet.name}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#5F6368]">Question</div>
              <div className="text-sm sm:text-base font-extrabold text-[#111111]">
                {currentIndex + 1} <span className="text-[#5F6368] font-normal text-xs">/ {setQuestions.length}</span>
              </div>
            </div>
            <button
              onClick={() => setIsSubmitModalOpen(true)}
              className="px-3.5 py-2 rounded-xl bg-[#111111] hover:bg-[#222222] text-white text-xs font-bold transition-all cursor-pointer shadow-2xs"
            >
              Finish ({totalAnswered}/{setQuestions.length})
            </button>
          </div>
        </div>

        {/* Dynamic Progress Bar */}
        <div className="w-full bg-[#E8FAF9] rounded-full h-2 overflow-hidden">
          <div
            className="h-full rounded-full bg-[#56E8E1] transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* 2. Interactive Question Navigation Controls (Compact Pagination) */}
      <div className="bg-white rounded-2xl border border-[#E5E9E9] p-2.5 sm:p-3 shadow-[0_2px_8px_rgba(17,17,17,0.02)] flex items-center justify-between gap-2">
        <button
          onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
          disabled={currentIndex === 0}
          className="p-2 sm:px-3 sm:py-1.5 rounded-xl border border-[#E5E9E9] bg-white text-xs font-bold text-[#111111] hover:bg-[#F0F4F4] disabled:opacity-30 disabled:pointer-events-none transition-all flex items-center gap-1 cursor-pointer shrink-0"
          title="Previous question"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Prev</span>
        </button>

        <div className="flex items-center gap-1 sm:gap-1.5 overflow-x-auto no-scrollbar py-0.5 justify-center flex-1">
          {getVisiblePages().map((item, i) => {
            if (item === 'ellipsis-left' || item === 'ellipsis-right') {
              return (
                <span key={`${item}-${i}`} className="text-xs text-[#5F6368] font-bold px-1 select-none">
                  ...
                </span>
              );
            }

            const idx = item as number;
            const q = setQuestions[idx];
            const hasAns = selectedAnswers[q.id] !== undefined;
            const isCurr = idx === currentIndex;
            const isAnsCorrect = hasAns && selectedAnswers[q.id] === q.correct_option;

            let btnStyle = 'bg-[#F0F4F4] text-[#5F6368] hover:bg-[#E8FAF9] hover:text-[#111111]';
            if (isCurr) {
              btnStyle = 'ring-2 ring-[#56E8E1] bg-[#111111] text-white font-extrabold shadow-xs';
            } else if (hasAns) {
              btnStyle = isAnsCorrect
                ? 'bg-[#EAF8F1] text-[#20A66A] font-bold border border-[#20A66A]/40'
                : 'bg-[#FDEDEF] text-[#D94A5C] font-bold border border-[#D94A5C]/40';
            }

            return (
              <button
                key={q.id}
                onClick={() => setCurrentIndex(idx)}
                className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl text-xs flex items-center justify-center transition-all cursor-pointer shrink-0 ${btnStyle}`}
              >
                {idx + 1}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => setCurrentIndex((prev) => Math.min(setQuestions.length - 1, prev + 1))}
          disabled={currentIndex === setQuestions.length - 1}
          className="p-2 sm:px-3 sm:py-1.5 rounded-xl border border-[#E5E9E9] bg-white text-xs font-bold text-[#111111] hover:bg-[#F0F4F4] disabled:opacity-30 disabled:pointer-events-none transition-all flex items-center gap-1 cursor-pointer shrink-0"
          title="Next question"
        >
          <span className="hidden sm:inline">Next</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* 3. Main Question Card */}
      <div className="bg-white rounded-3xl border border-[#E5E9E9] overflow-hidden shadow-[0_4px_20px_rgba(17,17,17,0.03)]">
        {/* Question Header */}
        <div className="p-4 sm:p-5 border-b border-[#E5E9E9] flex items-center justify-between gap-3 bg-[#F7FAFA]">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-xl bg-[#E8FAF9] text-[#1b8e88] font-extrabold text-sm flex items-center justify-center border border-[#56E8E1]/40">
              {currentIndex + 1}
            </span>
            <span className="text-xs font-semibold text-[#5F6368]">
              Single Choice MCQ
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* English | हिन्दी Language Segmented Control */}
            <div className="flex items-center bg-[#F0F4F4] p-0.5 rounded-lg border border-[#E5E9E9]">
              <button
                type="button"
                onClick={() => setQuestionLanguage('en')}
                className={`px-2.5 py-1 rounded-md text-xs font-extrabold transition-all cursor-pointer ${
                  questionLanguage === 'en'
                    ? 'bg-[#111111] text-white shadow-xs'
                    : 'text-[#5F6368] hover:text-[#111111]'
                }`}
              >
                English
              </button>
              <button
                type="button"
                onClick={() => setQuestionLanguage('hi')}
                className={`px-2.5 py-1 rounded-md text-xs font-extrabold transition-all cursor-pointer ${
                  questionLanguage === 'hi'
                    ? 'bg-[#111111] text-white shadow-xs'
                    : 'text-[#5F6368] hover:text-[#111111]'
                }`}
              >
                हिन्दी
              </button>
            </div>

            <span
              className={`px-2.5 py-1 rounded-lg text-[11px] font-bold ${
                currentQ.difficulty === 'Easy'
                  ? 'bg-[#EAF8F1] text-[#20A66A] border border-[#20A66A]/30'
                  : currentQ.difficulty === 'Medium'
                  ? 'bg-[#FFF7D9] text-[#8a6508] border border-[#EBC138]/40'
                  : 'bg-[#FDEDEF] text-[#D94A5C] border border-[#D94A5C]/30'
              }`}
            >
              {currentQ.difficulty}
            </span>

            {isAnswered && (
              <button
                onClick={handleResetCurrent}
                className="px-2.5 py-1 rounded-lg text-xs font-semibold text-[#5F6368] hover:text-[#111111] hover:bg-[#F0F4F4] transition-colors flex items-center gap-1 cursor-pointer"
                title="Reset choice"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Retry</span>
              </button>
            )}
          </div>
        </div>

        {/* Question Text & Options */}
        <div className="p-5 sm:p-6">
          <h2 className="text-base sm:text-lg font-bold text-[#111111] leading-relaxed mb-4">
            {getQuestionText()}
          </h2>

          {/* Options List */}
          <div className="space-y-2.5">
            {optionsList.map(({ key, text }) => {
              const isSelected = selectedOpt === key;
              const isThisCorrect = key === currentQ.correct_option;

              let optionStyle =
                'border-[#E5E9E9] bg-white hover:border-[#56E8E1]/60 hover:bg-[#E8FAF9]/20 text-[#111111]';
              let badgeStyle = 'bg-[#F0F4F4] text-[#5F6368] border-[#E5E9E9]';

              if (isAnswered) {
                if (isThisCorrect) {
                  optionStyle = 'border-[#20A66A] bg-[#EAF8F1] text-[#111111] font-semibold shadow-2xs';
                  badgeStyle = 'bg-[#20A66A] text-white border-[#20A66A]';
                } else if (isSelected && !isThisCorrect) {
                  optionStyle = 'border-[#D94A5C] bg-[#FDEDEF] text-[#111111] font-semibold';
                  badgeStyle = 'bg-[#D94A5C] text-white border-[#D94A5C]';
                } else {
                  optionStyle = 'border-[#E5E9E9]/60 opacity-50 text-[#5F6368]';
                }
              }

              return (
                <button
                  key={key}
                  onClick={() => handleSelectOption(key)}
                  className={`w-full text-left px-3.5 py-2.5 sm:py-3 rounded-xl border-2 transition-all flex items-center gap-3 focus:outline-none cursor-pointer ${optionStyle}`}
                >
                  <span
                    className={`w-6 h-6 sm:w-6.5 sm:h-6.5 rounded-lg text-xs font-bold flex items-center justify-center shrink-0 border transition-colors ${badgeStyle}`}
                  >
                    {isAnswered && isThisCorrect ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : isAnswered && isSelected && !isThisCorrect ? (
                      <XCircle className="w-3.5 h-3.5" />
                    ) : (
                      key
                    )}
                  </span>
                  <span className="text-xs sm:text-sm leading-snug flex-1">
                    {text}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Instant Conceptual Explanation Card (Always Bilingual) */}
          {isAnswered && (
            <div className="mt-6 pt-5 border-t border-[#E5E9E9] animate-in fade-in slide-in-from-top-2 duration-200">
              {/* Feedback Summary Banner */}
              <div
                className={`p-3.5 sm:p-4 rounded-xl border mb-4 flex items-center gap-3 ${
                  isCorrect
                    ? 'bg-[#EAF8F1] border-[#20A66A]/30 text-[#111111]'
                    : 'bg-[#FDEDEF] border-[#D94A5C]/30 text-[#111111]'
                }`}
              >
                {isCorrect ? (
                  <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-[#20A66A] shrink-0" />
                ) : (
                  <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-[#D94A5C] shrink-0" />
                )}
                <div>
                  <div className="font-extrabold text-xs sm:text-sm">
                    {isCorrect ? 'Correct Answer! Great job.' : 'Incorrect Answer.'}
                  </div>
                  <div className="text-xs mt-0.5 text-[#5F6368]">
                    {isCorrect ? (
                      <span>You selected Option {selectedOpt}.</span>
                    ) : (
                      <span>
                        Your answer: <strong className="text-[#D94A5C]">Option {selectedOpt}</strong> • Correct answer is{' '}
                        <strong className="underline decoration-2 text-[#20A66A]">Option {currentQ.correct_option}</strong>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Detailed Explanation Container */}
              <div className="bg-[#F7FAFA] p-4 sm:p-5 rounded-2xl border border-[#E5E9E9] space-y-4">
                <div className="flex items-center gap-2 text-[#1b8e88] text-xs font-bold uppercase tracking-wider">
                  <Lightbulb className="w-4 h-4 text-[#1fb4ac]" />
                  Conceptual Explanation & Key Learning
                </div>

                {/* English Block */}
                {currentQ.explanation?.trim() && (
                  <div className="space-y-1">
                    <div className="text-[11px] font-extrabold text-[#5F6368] uppercase tracking-wider">English</div>
                    <p className="text-xs sm:text-sm leading-relaxed text-[#111111] whitespace-pre-line font-normal">
                      {currentQ.explanation}
                    </p>
                  </div>
                )}

                {/* Hindi Block (Only rendered when explanation_hi is present) */}
                {currentQ.explanation_hi?.trim() && (
                  <div className="space-y-1 pt-2 border-t border-[#E5E9E9]">
                    <div className="text-[11px] font-extrabold text-[#1b8e88] uppercase tracking-wider">हिंदी</div>
                    <p className="text-xs sm:text-sm leading-relaxed text-[#111111] whitespace-pre-line font-normal">
                      {currentQ.explanation_hi}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Bottom Controls */}
        <div className="p-4 sm:p-5 bg-[#F7FAFA] border-t border-[#E5E9E9] flex items-center justify-between gap-3">
          <button
            onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
            disabled={currentIndex === 0}
            className="px-4 py-2.5 rounded-xl border border-[#E5E9E9] bg-white text-xs font-bold text-[#111111] hover:bg-[#F0F4F4] disabled:opacity-40 disabled:pointer-events-none transition-all flex items-center gap-1.5 cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </button>

          <div className="text-xs text-[#5F6368] font-semibold hidden sm:block">
            {totalAnswered} of {setQuestions.length} answered
          </div>

          {currentIndex < setQuestions.length - 1 ? (
            <button
              onClick={() => setCurrentIndex((prev) => prev + 1)}
              className="px-5 py-2.5 rounded-xl bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] text-xs font-bold transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
            >
              Next Question
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => setIsSubmitModalOpen(true)}
              className="px-5 py-2.5 rounded-xl bg-[#20A66A] hover:bg-[#1a8e5a] text-white text-xs font-bold transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
            >
              Complete Revision
              <CheckCircle2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Exit Confirmation Modal */}
      <ConfirmModal
        isOpen={showExitConfirm}
        title="Return to Dashboard"
        message="Return to dashboard? Your current progress will be recorded."
        confirmText="Confirm & Exit"
        variant="warning"
        onConfirm={() => {
          handleFinish('student-dashboard');
        }}
        onCancel={() => setShowExitConfirm(false)}
      />

      {/* Submit Revision Confirmation Modal */}
      <ConfirmModal
        isOpen={isSubmitModalOpen}
        title="Ready to Submit Revision?"
        message={`You have answered ${totalAnswered} of ${setQuestions.length} questions. Submitting will save your progress and record your score.`}
        confirmText={isSubmitting ? 'Submitting...' : 'Yes, Submit Now'}
        cancelText="Continue Answering"
        variant="info"
        onConfirm={() => handleFinish('quiz-scorecard')}
        onCancel={() => setIsSubmitModalOpen(false)}
      />
    </div>
  );
};

export default QuizRevisionView;
